<!--
  Sub-componente compartilhado de cards de pagamento por moeda.

  Renderizacao data-driven via getter `cardsExibidos`, que aplica a "regra
  de ancora dinamica" (ver `docs/ai/REGRA_ANCORA_MOEDA_FORMA_PAGAMENTO.md`):
    - R1: moeda da venda (`moedaPadrao`) aparece primeiro;
    - R6: cards nao pareados sao ocultados (matriz `moedasVisiveis`);
    - defesa em profundidade ARS: tambem requer `pesoHabilitado`.

  Para cada item da lista, escolhemos o ng-template correspondente a moeda
  via *ngTemplateOutlet, passando a configuracao do header (`card`) com
  taxa+simbolo+digits+locale ja resolvidos.
-->
<div class="money-cards money-cards--multi">
  <ng-container *ngFor="let card of cardsExibidos; trackBy: trackByMoeda">
    <ng-container [ngSwitch]="card.moeda">
      <ng-container *ngSwitchCase="'PYG'"><ng-container *ngTemplateOutlet="moneyCardPYG; context: { $implicit: card }"></ng-container></ng-container>
      <ng-container *ngSwitchCase="'USD'"><ng-container *ngTemplateOutlet="moneyCardUSD; context: { $implicit: card }"></ng-container></ng-container>
      <ng-container *ngSwitchCase="'BRL'"><ng-container *ngTemplateOutlet="moneyCardBRL; context: { $implicit: card }"></ng-container></ng-container>
      <ng-container *ngSwitchCase="'ARS'"><ng-container *ngTemplateOutlet="moneyCardARS; context: { $implicit: card }"></ng-container></ng-container>
    </ng-container>
  </ng-container>
</div>

<!-- ===== TEMPLATES DE MONEY CARDS ===== -->

<!-- Card PYG -->
<ng-template #moneyCardPYG let-card>
  <div class="money-card" [class.money-card--active]="selectedMoneyCode==='PYG'">
    <div class="money-card-header">
      <div class="money-card-flag">
        <img src="assets/images/paraguay-flag.jpg" class="icon-flag" alt="Paraguay" />
      </div>
      <div class="money-card-info">
        <span class="money-card-name">Guarani</span>
        <span class="money-card-rate">{{ card.taxaNumero | moedaSimbolo: card.taxaSimbolo:card.taxaDigits:card.taxaLocale }}</span>
      </div>
      <div class="money-card-conversion">
        {{ remainingEm('PYG') }}
      </div>
    </div>
    <div class="money-card-fields">
      <div class="money-card-field">
        <label>{{ 'forma_pagamento.valor' | translate }}</label>
        <input
          #inputGuarani
          type="text"
          pInputText
          [value]="valorGuaraniStr"
          placeholder="Gs. 0"
          [disabled]="disabled"
          (focus)="setSelectedMoney('PYG')"
          (input)="onMoneyInput('PYG',$event)"
        />
      </div>
      <div class="money-card-field" *ngIf="mostrarTroco">
        <label>{{ 'forma_pagamento.troco' | translate }}</label>
        <input
          type="text"
          pInputText
          [value]="trocoGuaraniStr"
          placeholder="Gs. 0"
          [disabled]="disabled"
          (input)="onTrocoInput('PYG',$event)"
        />
      </div>
      <!-- Legenda de faltante/troco consolidada: ocupa a largura total do
           card (VALOR + VUELTO) para nao quebrar em duas linhas. Como
           `tipoDeFaltante` e 'pagamento' XOR 'troco', um unico <small>
           cobre ambos os casos. -->
      <small
        *ngIf="deveExibirFaltantePara('GUARANI') && tipoDeFaltante !== 'nenhum'"
        class="money-card-faltando">
        <ng-container *ngIf="tipoDeFaltante === 'pagamento'">
          {{ 'forma_pagamento.falta_informar' | translate }}: {{ faltanteParaFechar('GUARANI') | moedaSimbolo:'PYG':digitsMoedaPYG:'es-PY' }}
        </ng-container>
        <ng-container *ngIf="tipoDeFaltante === 'troco'">
          {{ 'forma_pagamento.troco_devido' | translate }}: {{ (faltanteParaFechar('GUARANI') * -1) | moedaSimbolo:'PYG':digitsMoedaPYG:'es-PY' }}
        </ng-container>
      </small>
    </div>
  </div>
</ng-template>

<!-- Card USD -->
<ng-template #moneyCardUSD let-card>
  <div class="money-card" [class.money-card--active]="selectedMoneyCode==='USD'">
    <div class="money-card-header">
      <div class="money-card-flag">
        <img src="assets/images/usa-flag.svg" class="icon-flag" alt="USA" />
      </div>
      <div class="money-card-info">
        <span class="money-card-name">Dolar</span>
        <span class="money-card-rate">{{ card.taxaNumero | moedaSimbolo: card.taxaSimbolo:card.taxaDigits:card.taxaLocale }}</span>
      </div>
      <div class="money-card-conversion">
        {{ remainingEm('USD') }}
      </div>
    </div>
    <div class="money-card-fields">
      <div class="money-card-field">
        <label>{{ 'forma_pagamento.valor' | translate }}</label>
        <input
          #inputDolar
          type="text"
          pInputText
          [value]="valorDolarStr"
          placeholder="$0.00"
          [disabled]="disabled"
          (focus)="setSelectedMoney('USD')"
          (input)="onMoneyInput('USD',$event)"
        />
      </div>
      <div class="money-card-field" *ngIf="mostrarTroco">
        <label>{{ 'forma_pagamento.troco' | translate }}</label>
        <input
          type="text"
          pInputText
          [value]="trocoDolarStr"
          placeholder="$0.00"
          [disabled]="disabled"
          (input)="onTrocoInput('USD',$event)"
        />
      </div>
      <!-- Legenda de faltante/troco consolidada: ocupa a largura total do
           card (VALOR + VUELTO) para nao quebrar em duas linhas. Como
           `tipoDeFaltante` e 'pagamento' XOR 'troco', um unico <small>
           cobre ambos os casos. -->
      <small
        *ngIf="deveExibirFaltantePara('DOLAR') && tipoDeFaltante !== 'nenhum'"
        class="money-card-faltando">
        <ng-container *ngIf="tipoDeFaltante === 'pagamento'">
          {{ 'forma_pagamento.falta_informar' | translate }}: {{ faltanteParaFechar('DOLAR') | moedaSimbolo:'USD':'1.2-2':'en-US' }}
        </ng-container>
        <ng-container *ngIf="tipoDeFaltante === 'troco'">
          {{ 'forma_pagamento.troco_devido' | translate }}: {{ (faltanteParaFechar('DOLAR') * -1) | moedaSimbolo:'USD':'1.2-2':'en-US' }}
        </ng-container>
      </small>
    </div>
  </div>
</ng-template>

<!-- Card ARS (Peso Argentino) -->
<ng-template #moneyCardARS let-card>
  <div class="money-card" [class.money-card--active]="selectedMoneyCode==='ARS'">
    <div class="money-card-header">
      <div class="money-card-flag">
        <img src="assets/images/argentina-flag.svg" class="icon-flag" alt="Argentina" />
      </div>
      <div class="money-card-info">
        <span class="money-card-name">{{ 'forma_pagamento.peso' | translate }}</span>
        <span class="money-card-rate">{{ card.taxaNumero | moedaSimbolo: card.taxaSimbolo:card.taxaDigits:card.taxaLocale }}</span>
      </div>
      <div class="money-card-conversion">
        {{ remainingEm('ARS') }}
      </div>
    </div>
    <div class="money-card-fields">
      <div class="money-card-field">
        <label>{{ 'forma_pagamento.valor' | translate }}</label>
        <input
          #inputPeso
          type="text"
          pInputText
          [value]="valorPesoStr"
          placeholder="$ 0,00"
          [disabled]="disabled"
          (focus)="setSelectedMoney('ARS')"
          (input)="onMoneyInput('ARS',$event)"
        />
      </div>
      <div class="money-card-field" *ngIf="mostrarTroco">
        <label>{{ 'forma_pagamento.troco' | translate }}</label>
        <input
          type="text"
          pInputText
          [value]="trocoPesoStr"
          placeholder="$ 0,00"
          [disabled]="disabled"
          (input)="onTrocoInput('ARS',$event)"
        />
      </div>
      <!-- Legenda de faltante/troco consolidada: ocupa a largura total do
           card (VALOR + VUELTO) para nao quebrar em duas linhas. Como
           `tipoDeFaltante` e 'pagamento' XOR 'troco', um unico <small>
           cobre ambos os casos. -->
      <small
        *ngIf="deveExibirFaltantePara('PESO') && tipoDeFaltante !== 'nenhum'"
        class="money-card-faltando">
        <ng-container *ngIf="tipoDeFaltante === 'pagamento'">
          {{ 'forma_pagamento.falta_informar' | translate }}: {{ faltanteParaFechar('PESO') | moedaSimbolo:'ARS':'1.2-2':'es-AR' }}
        </ng-container>
        <ng-container *ngIf="tipoDeFaltante === 'troco'">
          {{ 'forma_pagamento.troco_devido' | translate }}: {{ (faltanteParaFechar('PESO') * -1) | moedaSimbolo:'ARS':'1.2-2':'es-AR' }}
        </ng-container>
      </small>
    </div>
  </div>
</ng-template>

<!-- Card BRL -->
<ng-template #moneyCardBRL let-card>
  <div class="money-card" [class.money-card--active]="selectedMoneyCode==='BRL'">
    <div class="money-card-header">
      <div class="money-card-flag">
        <img src="assets/images/br-flag.svg" class="icon-flag" alt="BR" />
      </div>
      <div class="money-card-info">
        <span class="money-card-name">Real</span>
        <span class="money-card-rate">{{ card.taxaNumero | moedaSimbolo: card.taxaSimbolo:card.taxaDigits:card.taxaLocale }}</span>
      </div>
      <div class="money-card-conversion">
        {{ remainingEm('BRL') }}
      </div>
    </div>
    <div class="money-card-fields">
      <div class="money-card-field">
        <label>{{ 'forma_pagamento.valor' | translate }}</label>
        <input
          #inputReal
          type="text"
          pInputText
          [value]="valorRealStr"
          placeholder="R$ 0,00"
          [disabled]="disabled"
          (focus)="setSelectedMoney('BRL')"
          (input)="onMoneyInput('BRL',$event)"
        />
      </div>
      <div class="money-card-field" *ngIf="mostrarTroco">
        <label>{{ 'forma_pagamento.troco' | translate }}</label>
        <input
          type="text"
          pInputText
          [value]="trocoRealStr"
          placeholder="R$ 0,00"
          [disabled]="disabled"
          (input)="onTrocoInput('BRL',$event)"
        />
      </div>
      <!-- Legenda de faltante/troco consolidada: ocupa a largura total do
           card (VALOR + VUELTO) para nao quebrar em duas linhas. Como
           `tipoDeFaltante` e 'pagamento' XOR 'troco', um unico <small>
           cobre ambos os casos. -->
      <small
        *ngIf="deveExibirFaltantePara('REAL') && tipoDeFaltante !== 'nenhum'"
        class="money-card-faltando">
        <ng-container *ngIf="tipoDeFaltante === 'pagamento'">
          {{ 'forma_pagamento.falta_informar' | translate }}: {{ faltanteParaFechar('REAL') | moedaSimbolo:'BRL':'1.2-2':'pt-BR' }}
        </ng-container>
        <ng-container *ngIf="tipoDeFaltante === 'troco'">
          {{ 'forma_pagamento.troco_devido' | translate }}: {{ (faltanteParaFechar('REAL') * -1) | moedaSimbolo:'BRL':'1.2-2':'pt-BR' }}
        </ng-container>
      </small>
    </div>
  </div>
</ng-template>
